import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import './apiKeys.scss';
import './companyMembers.scss';
import './companyRoles.scss';
import {
    getModules,
    getAppPermissions,
    createCompanyRole,
    updateCompanyRole,
    getCompanyRoles,
} from '../../Redux/ActionCreators/settingAction';
import { getJobApiCall } from '../../utils/jobRequest';
import { useAppPermission } from '../../hooks/useAppPermission';
import { P } from '../../constants/rbac';

const ACTION_ORDER = ['read', 'create', 'edit', 'delete', 'update', 'publish'];
const ACCORDION_MODULES = new Set(['settings']);

/**
 * Card order within / across modules on the Roles permission grid.
 * Jobs resources first (Assignment → Job → Automation → Settings), then Candidates, then the rest.
 */
const MODULE_CARD_ORDER = [
    'jobs',
    'candidates',
    'assessments',
    'messenger',
    'onboarding',
    'insights',
    'settings',
];

const JOBS_RESOURCE_ORDER = ['assignment', 'job', 'automation', 'settings'];

const resourceSortKey = (moduleCode, resource) => {
    const moduleIdx = MODULE_CARD_ORDER.indexOf(moduleCode);
    const moduleRank = moduleIdx === -1 ? 900 + String(moduleCode).charCodeAt(0) : moduleIdx * 100;
    if (moduleCode === 'jobs') {
        const rIdx = JOBS_RESOURCE_ORDER.indexOf(resource);
        return moduleRank + (rIdx === -1 ? 50 : rIdx);
    }
    return moduleRank;
};

const defaultForm = {
    name: '',
    code: '',
    description: '',
    data_scope: 'company',
    permission_codes: [],
};

const slugifyCode = (name) =>
    String(name || '')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 100);

const titleCase = (value) =>
    String(value || '')
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

/** User-facing labels for permission resources (API codes stay unchanged). */
const RESOURCE_DISPLAY_NAME = {
    company: 'Team',
    client_company: 'Company',
    job_board: 'Post to Job Boards',
    integration: 'Integrations',
};

const resourceDisplayName = (moduleCode, resource) => {
    if (moduleCode === 'settings' && RESOURCE_DISPLAY_NAME[resource]) {
        return RESOURCE_DISPLAY_NAME[resource];
    }
    return titleCase(resource);
};

/**
 * One card per module.resource (e.g. Jobs→Job, Jobs→Assignment, Settings→Roles).
 * Settings cards are marked for accordion grouping.
 */
const buildResourceCards = (modules, permissions) => {
    const moduleById = {};
    modules.forEach((m) => {
        moduleById[m.id] = m;
    });

    const buckets = {};
    permissions.forEach((p) => {
        const mod = moduleById[p.module_id];
        const moduleCode = mod?.code || 'other';
        const parts = String(p.code || '').split('.');
        const resource = parts[1] || 'general';
        const action = parts[2] || parts[parts.length - 1] || 'access';
        const key = `${moduleCode}::${resource}`;

        if (!buckets[key]) {
            buckets[key] = {
                id: key,
                moduleCode,
                moduleName: mod?.name || titleCase(moduleCode),
                resource,
                display_order: mod?.display_order ?? 999,
                actions: [],
            };
        }
        buckets[key].actions.push({
            ...p,
            action,
            actionLabel: titleCase(action),
        });
    });

    // Count resources per module to decide titles
    const resourceCountByModule = {};
    Object.values(buckets).forEach((b) => {
        resourceCountByModule[b.moduleCode] = (resourceCountByModule[b.moduleCode] || 0) + 1;
    });

    return Object.values(buckets)
        .map((b) => {
            const multi = (resourceCountByModule[b.moduleCode] || 0) > 1;
            const title = multi ? resourceDisplayName(b.moduleCode, b.resource) : b.moduleName;
            const actions = b.actions.sort((a, c) => {
                const ai = ACTION_ORDER.indexOf(a.action);
                const bi = ACTION_ORDER.indexOf(c.action);
                return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
            });
            const allCodes = actions.map((a) => a.code);
            return {
                ...b,
                title,
                showModuleTag: multi,
                inAccordion: ACCORDION_MODULES.has(b.moduleCode),
                actions,
                allCodes,
                total: allCodes.length,
                sortKey: resourceSortKey(b.moduleCode, b.resource),
            };
        })
        .sort((a, b) => {
            if (a.sortKey !== b.sortKey) return a.sortKey - b.sortKey;
            return a.title.localeCompare(b.title);
        });
};

const PermissionCard = ({
    card,
    selectedCodes,
    readOnly,
    onToggle,
    onToggleAll,
}) => {
    const selected = card.allCodes.filter((c) => selectedCodes.includes(c)).length;
    const allSelected = selected === card.total && card.total > 0;
    const scopeHint = card.moduleCode === 'onboarding'
        ? 'Applies to Flows, Forms, Documents & Active Onboarding'
        : null;

    return (
        <div className="roles-module-card">
            <div className="roles-module-card-head">
                <div>
                    {card.showModuleTag && (
                        <div className="roles-module-tag">{card.moduleName}</div>
                    )}
                    <div className="roles-module-title">{card.title}</div>
                    {scopeHint && (
                        <div className="roles-module-hint">{scopeHint}</div>
                    )}
                </div>
                <div className={`roles-module-count${allSelected ? ' complete' : ''}`}>
                    {selected} / {card.total}
                </div>
            </div>

            <div className="roles-action-list">
                {card.actions.map((action) => (
                    <label
                        key={action.code}
                        className={`roles-action-row${readOnly ? ' disabled' : ''}`}
                    >
                        <input
                            type="checkbox"
                            checked={selectedCodes.includes(action.code)}
                            disabled={readOnly}
                            onChange={() => onToggle(action.code)}
                        />
                        <span>{action.actionLabel}</span>
                    </label>
                ))}
            </div>

            <div className="roles-module-footer">
                <button type="button" disabled={readOnly} onClick={() => onToggleAll(card)}>
                    {allSelected ? 'Clear all' : 'Select all'}
                </button>
            </div>
        </div>
    );
};

const CreateCompanyRole = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { roleId } = useParams();
    const isEdit = Boolean(roleId);
    const { can } = useAppPermission();
    const canSave = isEdit ? can(P.SETTINGS_ROLES_EDIT) : can(P.SETTINGS_ROLES_CREATE);

    const modules = useSelector((state) => state.setting.modules || []);
    const appPermissions = useSelector((state) => state.setting.appPermissions || []);

    const [form, setForm] = useState(defaultForm);
    const [formErrors, setFormErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [roleMeta, setRoleMeta] = useState(location.state?.role || null);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
    const [settingsOpen, setSettingsOpen] = useState(true);

    const isSystemEdit = !!(roleMeta && roleMeta.is_system);
    const readOnlyPerms = isSystemEdit;

    const resourceCards = useMemo(
        () => buildResourceCards(modules, appPermissions),
        [modules, appPermissions]
    );

    const gridCards = useMemo(
        () => resourceCards.filter((c) => !c.inAccordion),
        [resourceCards]
    );

    const settingsCards = useMemo(
        () => resourceCards.filter((c) => c.inAccordion),
        [resourceCards]
    );

    const selectedCount = form.permission_codes.length;
    const totalCount = resourceCards.reduce((sum, m) => sum + m.total, 0);

    const settingsSelected = settingsCards.reduce(
        (sum, c) => sum + c.allCodes.filter((code) => form.permission_codes.includes(code)).length,
        0
    );
    const settingsTotal = settingsCards.reduce((sum, c) => sum + c.total, 0);

    const goBackToRoles = (message) => {
        navigate('/company-roles', {
            state: { flash: message || null },
        });
    };

    useEffect(() => {
        dispatch(getModules());
        dispatch(getAppPermissions());
    }, [dispatch]);

    useEffect(() => {
        if (!isEdit) return;
        const hydrate = async () => {
            setLoading(true);
            try {
                let role = location.state?.role || null;
                if (!role) {
                    const response = await getJobApiCall(`app-rbac/roles/${roleId}`);
                    role = response?.data?.data ?? response?.data ?? null;
                }
                if (!role) {
                    setSnack({ open: true, message: 'Role not found.', severity: 'error' });
                    setLoading(false);
                    return;
                }
                setRoleMeta(role);
                setForm({
                    name: role.name || '',
                    code: role.code || '',
                    description: role.description || '',
                    data_scope: role.data_scope || 'company',
                    permission_codes: Array.isArray(role.permission_codes) ? [...role.permission_codes] : [],
                });
            } catch {
                setSnack({ open: true, message: 'Failed to load role.', severity: 'error' });
            } finally {
                setLoading(false);
            }
        };
        hydrate();
    }, [isEdit, roleId, location.state]);

    const togglePermission = (code) => {
        if (readOnlyPerms) return;
        setForm((prev) => {
            const set = new Set(prev.permission_codes);
            const card = resourceCards.find((c) => c.allCodes.includes(code));
            const readCode = card?.actions?.find((a) => a.action === 'read')?.code;
            const writeCodes = (card?.actions || [])
                .filter((a) => a.action !== 'read')
                .map((a) => a.code);

            if (set.has(code)) {
                set.delete(code);
                // Clearing Read also clears Create/Edit/Delete on the same card
                if (readCode && code === readCode) {
                    writeCodes.forEach((c) => set.delete(c));
                }
            } else {
                set.add(code);
                // Granting Create/Edit/Delete always keeps Read on the same card
                if (readCode && writeCodes.includes(code)) {
                    set.add(readCode);
                }
            }
            return { ...prev, permission_codes: Array.from(set) };
        });
    };

    const toggleModuleAll = (moduleCard) => {
        if (readOnlyPerms) return;
        const codes = moduleCard.allCodes;
        setForm((prev) => {
            const set = new Set(prev.permission_codes);
            const allSelected = codes.every((c) => set.has(c));
            codes.forEach((c) => (allSelected ? set.delete(c) : set.add(c)));
            return { ...prev, permission_codes: Array.from(set) };
        });
    };

    const validate = () => {
        const errors = {};
        if (!form.name.trim()) errors.name = 'Role name is required';
        if (!isEdit && !form.code.trim() && !slugifyCode(form.name)) {
            errors.code = 'Role code is required';
        }
        return errors;
    };

    const handleSave = async () => {
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        setSaving(true);

        if (isEdit) {
            const payload = isSystemEdit
                ? { description: form.description.trim() || null, is_active: true }
                : {
                    name: form.name.trim(),
                    description: form.description.trim() || null,
                    data_scope: form.data_scope,
                    permission_codes: form.permission_codes,
                    is_active: true,
                };
            const result = await dispatch(updateCompanyRole(roleId, payload));
            if (result?.type === 'UPDATE_COMPANY_ROLE') {
                await dispatch(getCompanyRoles());
                goBackToRoles('Role updated.');
            } else {
                setSnack({
                    open: true,
                    message: result?.payload?.detail || 'Failed to update role.',
                    severity: 'error',
                });
            }
        } else {
            const code = form.code.trim() || slugifyCode(form.name);
            const result = await dispatch(
                createCompanyRole({
                    name: form.name.trim(),
                    code,
                    description: form.description.trim() || null,
                    data_scope: form.data_scope,
                    permission_codes: form.permission_codes,
                    is_active: true,
                })
            );
            if (result?.type === 'CREATE_COMPANY_ROLE') {
                await dispatch(getCompanyRoles());
                goBackToRoles('Role created.');
            } else {
                setSnack({
                    open: true,
                    message: result?.payload?.detail || 'Failed to create role.',
                    severity: 'error',
                });
            }
        }
        setSaving(false);
    };

    const pageTitle = isEdit
        ? (roleMeta?.name || 'Edit Role')
        : 'Create Custom Role';

    return (
        <div className="job-table-container roles-page">
            <button type="button" className="roles-back-btn" onClick={() => goBackToRoles()}>
                ← Back to Roles
            </button>

            <div className="roles-page-header">
                <div className="roles-page-titles">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <h4>{pageTitle}</h4>
                        {isSystemEdit && <span className="roles-badge system">SYSTEM</span>}
                        {isEdit && !isSystemEdit && <span className="roles-badge custom">CUSTOM</span>}
                    </div>
                    <p>
                        {isSystemEdit
                            ? 'System role permissions are managed by seed. You can update the description.'
                            : 'Configure data scope and module-level access for this role.'}
                    </p>
                </div>
                <div className="roles-header-actions">
                    {canSave && (
                        <button
                            type="button"
                            className="roles-btn-primary"
                            onClick={handleSave}
                            disabled={saving || loading}
                        >
                            {saving ? 'Saving...' : isEdit ? 'Save Configuration' : 'Create Role'}
                        </button>
                    )}
                </div>
            </div>

            <div className="roles-panel">
                {loading ? (
                    <div className="jv2-loading-cell" style={{ padding: 40, textAlign: 'center' }}>
                        <div
                            className="spinner-border"
                            role="status"
                            style={{ color: '#F75891', width: '28px', height: '28px' }}
                        >
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="roles-form-grid">
                            <div className="automation-dropdown">
                                <label>Role Name *</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    disabled={isSystemEdit}
                                    placeholder="e.g. Hiring Lead"
                                    onChange={(e) => {
                                        const name = e.target.value;
                                        setForm((prev) => ({
                                            ...prev,
                                            name,
                                            code: isEdit ? prev.code : slugifyCode(name),
                                        }));
                                        setFormErrors({ ...formErrors, name: '' });
                                    }}
                                />
                                {formErrors.name && <span className="field-error">{formErrors.name}</span>}
                            </div>

                            <div className="automation-dropdown">
                                <label>Role Code *</label>
                                <input
                                    type="text"
                                    value={form.code}
                                    disabled={isEdit}
                                    placeholder="Auto from name"
                                    onChange={(e) => {
                                        setForm({ ...form, code: slugifyCode(e.target.value) });
                                        setFormErrors({ ...formErrors, code: '' });
                                    }}
                                />
                                {formErrors.code && <span className="field-error">{formErrors.code}</span>}
                            </div>

                            <div className="automation-dropdown full">
                                <label>Description</label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    placeholder="Short summary of what this role can do"
                                />
                            </div>
                        </div>

                        <div className="roles-scope-card">
                            <h5>Data Scope</h5>
                            <p className="roles-scope-hint">
                                Controls which jobs and candidates this role can see.
                            </p>
                            <div className="roles-scope-options">
                                <button
                                    type="button"
                                    className={`roles-scope-option${form.data_scope === 'company' ? ' active' : ''}`}
                                    disabled={readOnlyPerms}
                                    onClick={() => !readOnlyPerms && setForm({ ...form, data_scope: 'company' })}
                                >
                                    <strong>Team-wide</strong>
                                    <span>All jobs in the team</span>
                                </button>
                                <button
                                    type="button"
                                    className={`roles-scope-option${form.data_scope === 'assigned' ? ' active' : ''}`}
                                    disabled={readOnlyPerms}
                                    onClick={() => !readOnlyPerms && setForm({ ...form, data_scope: 'assigned' })}
                                >
                                    <strong>Assigned jobs</strong>
                                    <span>Only jobs assigned via job ACL</span>
                                </button>
                            </div>
                        </div>

                        <div className="roles-modules-head">
                            <h5>Module access</h5>
                            <span>
                                {selectedCount} / {totalCount} permissions selected
                            </span>
                        </div>

                        {resourceCards.length === 0 ? (
                            <div className="roles-empty-hint">
                                No permissions loaded. Open the Roles page once so the catalog can seed.
                            </div>
                        ) : (
                            <>
                                <div className="roles-module-grid">
                                    {gridCards.map((card) => (
                                        <PermissionCard
                                            key={card.id}
                                            card={card}
                                            selectedCodes={form.permission_codes}
                                            readOnly={readOnlyPerms}
                                            onToggle={togglePermission}
                                            onToggleAll={toggleModuleAll}
                                        />
                                    ))}
                                </div>

                                {settingsCards.length > 0 && (
                                    <div className={`roles-accordion${settingsOpen ? ' open' : ''}`}>
                                        <button
                                            type="button"
                                            className="roles-accordion-head"
                                            onClick={() => setSettingsOpen((v) => !v)}
                                        >
                                            <div className="roles-accordion-titles">
                                                <strong>Settings</strong>
                                                <span>
                                                    {settingsSelected} / {settingsTotal} selected
                                                </span>
                                            </div>
                                            <span className="roles-accordion-chevron">{settingsOpen ? '▾' : '▸'}</span>
                                        </button>
                                        {settingsOpen && (
                                            <div className="roles-accordion-body">
                                                <div className="roles-module-grid">
                                                    {settingsCards.map((card) => (
                                                        <PermissionCard
                                                            key={card.id}
                                                            card={card}
                                                            selectedCodes={form.permission_codes}
                                                            readOnly={readOnlyPerms}
                                                            onToggle={togglePermission}
                                                            onToggleAll={toggleModuleAll}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        <div className="roles-footer-actions">
                            <button
                                type="button"
                                className="roles-btn-secondary"
                                onClick={() => goBackToRoles()}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            {canSave && (
                                <button
                                    type="button"
                                    className="roles-btn-primary"
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : isEdit ? 'Save Configuration' : 'Create Role'}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
            >
                <Alert severity={snack.severity}>{snack.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default CreateCompanyRole;
